import {
  Button,
  Divider,
  Group,
  MediaQuery,
  Modal,
  Paper,
  Space,
  Stack,
  Text,
  Title,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { TouchBackend } from 'react-dnd-touch-backend';

import { useClipboard, useMediaQuery } from '@mantine/hooks';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { IconGripVertical, IconHeart } from '@tabler/icons';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Head from 'next/head';
import { GridContextProvider, GridDropZone } from '../../src/GridDnD';
import { Database } from '../../types/supabase';
import { getDrakeImage } from '../../components/drake/genImage';
import SongGrid, { SongData, SONG_DATA } from '../../components/drake/SongGrid';
import { useLog } from '../../LogEventContext';

const DrakeKendrickList = () => {
  const theme = useMantineTheme();

  const [viewCount, setViewCount] = useState(1);
  const [likeCount, setLikeCount] = useState(0);
  const clipboard = useClipboard({ timeout: 500 });
  const supabase = useSupabaseClient<Database, any>();
  const [childLikedPost, setChildLikedPost] = useState(false);
  const [childLikeCount, setChildLikeCount] = useState(0);
  const [isLoading_React, setIsLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [songPlaying, setSongPlaying] = useState<SongData | null>(null);
  const [shareImage, setShareImage] = useState<string | null>(null);

  const [songs, setSongs] = useState<SongData[]>(SONG_DATA);

  const onListen = useCallback((song: SongData) => {
    setSongPlaying(song);
    setPlayerOpen(true);
  }, []);

  const onShare = useCallback(
    async (type: string) => {
      const image = (await getDrakeImage(songs.map((song) => song.id))) as string;
      console.log(image);
      setShareImage(image);
      setDownloadOpen(true);
    },
    [songs]
  );

  const { LogEvent } = useLog();

  const isSmall = useMediaQuery(`(max-width: 768px)`);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, [isSmall]);

  const metaTitle = 'Drake vs Kendrick Diss Tracks Ranking | Lomo';
  const metaDescription =
    'Drake vs Kendrick Lamar is the greatest rap beef of all time. With both artists dropping back to back hit songs its hard to keep track of them all. Create a ranking of your favorite tracks and share it with the world.';
  const metaUrl = 'https://lomolist.com/drake-kendrick';
  const thumbnailUrl =
    'https://res.cloudinary.com/dazwytwuw/image/upload/s--LuxzoVKW--/c_scale,q_65,w_696/v1715098504/kendrick-lamar-and-drake-rap-beef_schmu0.webp';

  return (
    <Paper p="xs" mb={100} maw={theme.breakpoints.xl} mx="auto">
      <Head>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:image" content={thumbnailUrl} />
        <meta name="og:description" content={metaDescription} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={metaUrl} />
        <meta property="twitter:site" content="@SethSetse" />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:image" content={thumbnailUrl} />
        <meta property="og:site_name" content="LoMo" />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={metaUrl} />
      </Head>
      <Group align="flex-start" mb={50}>
        <MediaQuery
          styles={{
            display: 'none',
          }}
          query="(max-width: 600px)"
        >
          <Stack>
            <Image
              alt="Image Alt"
              src="https://res.cloudinary.com/dazwytwuw/image/upload/s--LuxzoVKW--/c_scale,q_65,w_696/v1715098504/kendrick-lamar-and-drake-rap-beef_schmu0.webp"
              objectFit="contain" // Scale your image down to fit into the container
              width={287}
              height={200}
              priority
            />
            <Text weight={700} size="xs" lineClamp={4} color="dimmed" maw={287}>
              (Photo: Frazer Harrison/Getty Images, Kevin Winter/Getty Images)
            </Text>
          </Stack>
        </MediaQuery>
        <Stack align="flex-start">
          <Title>
            <span
              style={{
                color: '#B197FC',
              }}
            >
              Drake
            </span>
            {' vs '}
            <span
              style={{
                color: theme.colors.pink[6],
              }}
            >
              Kendrick Lamar
            </span>{' '}
            Diss Tracks{' '}
          </Title>
          <Text weight={700} size="sm" color="dimmed" maw={600}>
            Drake vs Kendrick Lamar is the greatest rap beef of all time. With both artists dropping
            back to back hit songs its hard to keep track of them all. Create a ranking of your
            favorite tracks and share it with the world.
          </Text>
        </Stack>
        <Space h="xs" />
        <MediaQuery
          styles={{
            display: 'none',
          }}
          query="(max-width: 600px)"
        >
          <Divider mx="md" orientation="vertical" size="3" />
        </MediaQuery>
        <Stack sx={{ gap: 0 }}>
          <Space sx={{ flex: 1 }} />
          <Button
            variant="gradient"
            my={8}
            size="sm"
            onClick={() => {
              window.open(
                `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  `Create a Drake vs Kendrick Lamar Diss Tracks Ranking with Lomo.`
                )}&hashtags=LomoList&url=${encodeURIComponent(
                  'https://lomolist.com/drake-kendrick'
                )}`
              );
            }}
          >
            Share to Twitter
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onShare('d');
            }}
          >
            Download Image
          </Button>
        </Stack>
      </Group>

      <Text>
        Drag the
        <span
          style={{
            position: 'relative',
            top: 5,
          }}
        >
          <IconGripVertical
            size={20}
            style={{
              top: -25,
            }}
          />
        </span>
        to rank the songs. Press Download Image when you&apos;re done
      </Text>

      {isReady && (
        <>
          {!isSmall && (
            <DndProvider backend={HTML5Backend}>
              <SongGrid songs={songs} setSongs={setSongs} onListen={onListen} />
            </DndProvider>
          )}
          {isSmall && (
            <DndProvider
              backend={TouchBackend}
              options={{
                enableMouseEvents: true,
              }}
            >
              <SongGrid songs={songs} setSongs={setSongs} onListen={onListen} />
            </DndProvider>
          )}
        </>
      )}

      <Modal
        opened={downloadOpen}
        onClose={() => {
          setDownloadOpen(false);
        }}
        title="Download Image"
        w={520}
        size="lg"
        centered
      >
        {shareImage && (
          <div
            style={{
              width: '90%',
              maxWidth: 400,
              aspectRatio: '9 / 16',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Image
              alt="share"
              onClick={() => {
                setShareImage(null);
                onShare('test');
              }}
              src={shareImage}
              fill
              style={{
                objectFit: 'contain',
                userSelect: 'none',
              }}
            />
          </div>
        )}

        <MediaQuery
          styles={{
            display: 'none',
          }}
          query="(min-width: 600px)"
        >
          <Text my="md"> Tap and hold to save the image.</Text>
        </MediaQuery>

        <Button
          onClick={() => {
            const link = document.createElement('a');
            link.download = `Drake_Vs_Kendrick_List.png`;
            link.href = shareImage ?? '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            LogEvent('share_image', 'download');
          }}
        >
          Download Image
        </Button>
      </Modal>

      <Modal
        opened={playerOpen}
        onClose={() => {
          setPlayerOpen(false);
        }}
        title={`${songPlaying?.name} - ${songPlaying?.artist}`}
        w={1000}
        size="xl"
        centered
      >
        {songPlaying && (
          <iframe
            width={isSmall ? '100%' : 600}
            height="400"
            src={songPlaying.songLink}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}
      </Modal>
    </Paper>
  );
};

export default DrakeKendrickList;
